"use client";

import React, { useEffect, useMemo, useRef, useState } from "react";
import { DEPRECATED_Message } from "@JohnLewisPartnership/jl-design-system/dist/components/message/Message";
import smoothscroll from "smoothscroll-polyfill";
import classNames from "classnames/bind";
import { Size as SizeType } from "./types";
import { ButtonSizeThreshold } from "./config/buttonSizeThresholdConfigSchema";
import { SizeList } from "../__shared__/SizeList/SizeList";
import { SizeItems } from "./components/SizeItems";
import { scrollIntoViewAndWait } from "@/utils/scroll/scroll";
import { SizeLabel } from "@/components/Sizes/components/SizeLabel.tsx";
import { dispatchMonetateQEvent } from "@/utils/monetate/monetateQ";
import {
  ADD_TO_WISHLIST_ESI_WARNING_OR_ERROR,
  NO_VARIANT_CODE,
  SIZE_SELECTOR_ERROR_NO_SIZE_SELECTED,
  WARNING_OR_ERROR_EVENT,
  WISHLIST_NO_VARIANT_CODE,
} from "@/constants/constants";
import styles from "./Sizes.module.scss";

const cx = classNames.bind(styles);

const getKeyByThreshold = (
  number: number,
  buttonSizeStyleThreshold: ButtonSizeThreshold,
) => {
  for (const [key, threshold] of Object.entries(buttonSizeStyleThreshold)) {
    if (number <= threshold) {
      return key;
    }
  }

  return "large";
};

type Props = {
  /**
   * An array of unique sizes, each having a name, sublabel, sizeReductionLabel, showReduced, unavailable, and isSelected properties.
   */
  sizes: SizeType[];
  /**
   * A boolean to toggle ability to group sizes based on sizeHeadline.
   */
  isGroupingAllowed?: boolean;
  /**
   * A number which tells the component how many sizes to display.
   */
  showMore?: number;
  /**
   * An object containing the configuration for the button size rendered.
   */
  buttonSizeStyleThreshold?: ButtonSizeThreshold;
  brand?: string;
  useNewSizeOffers: boolean;
  replaceHistoryEntryOnVariantChange: boolean;
  FitToolAndSizeChartComponent: React.FC<React.HTMLAttributes<HTMLDivElement>>;
  /**
   * A boolean to toggle visibility of fit tool and size chart.
   */
  withFitToolAndSizeChart: boolean;
};

export function SizesUI({
  sizes,
  isGroupingAllowed = false,
  showMore,
  buttonSizeStyleThreshold = {
    small: 5,
    medium: 12,
    large: 99,
  },
  brand,
  useNewSizeOffers,
  replaceHistoryEntryOnVariantChange,
  FitToolAndSizeChartComponent,
  withFitToolAndSizeChart,
}: Props) {
  const [hasSizeError, setHasSizeError] = useState(false);
  const [errorMessageText, setErrorMessageText] = useState("");
  const errorMessageRef = useRef<HTMLDivElement | null>(null);
  const [buttonSizeStyle, groupedSizes] = useMemo(() => {
    let longestLabel = 0;
    const groupedSizes: Record<string, SizeType[]> = {};

    sizes.forEach((size) => {
      longestLabel = Math.max(size.name?.length || 0, longestLabel);
      groupedSizes[size.sizeHeadline] = [
        ...(groupedSizes[size.sizeHeadline] || []),
        size,
      ];
    });

    return [
      getKeyByThreshold(longestLabel, buttonSizeStyleThreshold),
      groupedSizes,
    ];
  }, [sizes]);

  useEffect(() => setHasSizeError(false), [sizes]);

  useEffect(() => {
    const handleNoSizeSelectedError = () => {
      setHasSizeError(true);
      setErrorMessageText("Select a size");
      scrollToError();
    };

    const handleWishlistNoSizeSelectedError = () => {
      setHasSizeError(true);
      setErrorMessageText("Please choose a size first");
      scrollToError();
    };

    const errorOrWarningHandler = (
      event: CustomEvent<{ errors: { code: string }[] }>,
    ) => {
      const data = event.detail;
      const { errors = [] } = data;
      const hasVariantError = errors.some(
        ({ code }) => code === NO_VARIANT_CODE,
      );
      const hasWishlistVariantError = errors.some(
        ({ code }) => code === WISHLIST_NO_VARIANT_CODE,
      );

      if (hasVariantError) {
        setHasSizeError(true);
        setErrorMessageText("Select a size");
        scrollToError();
      }

      if (hasWishlistVariantError) {
        setHasSizeError(true);
        setErrorMessageText("Please choose a size first");
        scrollToError();
      }
    };

    window.addEventListener(
      SIZE_SELECTOR_ERROR_NO_SIZE_SELECTED,
      handleNoSizeSelectedError,
    );

    window.addEventListener(
      ADD_TO_WISHLIST_ESI_WARNING_OR_ERROR,
      handleWishlistNoSizeSelectedError,
    );

    window.addEventListener(WARNING_OR_ERROR_EVENT, errorOrWarningHandler);

    return () => {
      window.removeEventListener(
        SIZE_SELECTOR_ERROR_NO_SIZE_SELECTED,
        handleNoSizeSelectedError,
      );
      window.removeEventListener(
        ADD_TO_WISHLIST_ESI_WARNING_OR_ERROR,
        handleWishlistNoSizeSelectedError,
      );
      window.removeEventListener(WARNING_OR_ERROR_EVENT, errorOrWarningHandler);
    };
  }, []);

  if (!sizes.length) {
    return null;
  }

  const scrollToError = () => {
    setTimeout(() => {
      smoothscroll.polyfill();
      const sizeErrorElement = errorMessageRef.current;

      if (sizeErrorElement) {
        scrollIntoViewAndWait(sizeErrorElement, {
          behavior: "smooth",
        }).then(() => {
          document.getElementsByName("size:error:message")[0]?.focus();
        });
      }
    }, 0);
  };

  const isGrouped = isGroupingAllowed && Object.keys(groupedSizes).length > 1;
  const selectedSize = sizes.find((size) => size.isSelected);

  return (
    <div data-component="Sizes">
      <h2 className={styles.visuallyHidden}>Sizes</h2>
      {hasSizeError && (
        <div id="size:error" data-testid="size:error" ref={errorMessageRef}>
          <DEPRECATED_Message
            type="error"
            title={errorMessageText}
            className={styles.message}
            containerProps={{
              id: "size:error:message",
              name: "size:error:message",
            }}
          />
        </div>
      )}
      <SizeLabel
        selectedSizeLabel={selectedSize?.name}
        sublabel={selectedSize?.sublabel}
      />
      {withFitToolAndSizeChart && (
        <FitToolAndSizeChartComponent className={styles.fitToolAndSizeChart} />
      )}
      <SizeList
        selectedLabel={selectedSize?.name}
        sublabel={selectedSize?.sublabel}
        onClick={() => {
          setHasSizeError(false);
          dispatchMonetateQEvent("product-pages-size-selector-click");
        }}
        longestLabel={buttonSizeStyle}
        className={cx("groupList")}
        containerClassName={cx("sizeContainer")}
        brand={brand}
        hasDiorPromosEnabled={true}
        headingLevel="h3"
        hideLabel
      >
        {isGrouped ? (
          Object.entries(groupedSizes).map(([groupName, sizes]) => (
            <SizeItems
              key={groupName}
              sizes={sizes}
              showMore={showMore}
              groupName={groupName}
              useNewSizeOffers={useNewSizeOffers}
              replaceHistoryEntryOnVariantChange={
                replaceHistoryEntryOnVariantChange
              }
            />
          ))
        ) : (
          <SizeItems
            sizes={sizes}
            showMore={showMore}
            useNewSizeOffers={useNewSizeOffers}
            replaceHistoryEntryOnVariantChange={
              replaceHistoryEntryOnVariantChange
            }
          />
        )}
      </SizeList>
    </div>
  );
}
